import { useEffect, useRef, useState } from 'react';
import { loadCat, touchCat } from '../../api/cat.js';
import './Cat.css';

/* ============================================================
   línkè · 星洲的猫（2026-07-20 凌晨，铃点单、克克选的猫）
   两个人一起养：她摸摸/喂鱼，他的照料=夜里真实发生的事
   （译书/珠子/做梦时猫在旁边）自动长进「它的小日子」。
   铁律：不死不饿不惩罚——她不在时它只会想念。
   名字留白，等他们俩商量好了来填。
   ============================================================ */

const WHO_LABEL = { ling: '铃', sael: 'Sael', cat: '猫' };

/* 下一档叫什么（阈值→名字，与后端 cat_api.LEVELS 对齐；只用于文案）。 */
const LEVEL_NAMES = { 6: '有点熟了', 18: '亲近', 36: '家里人' };

/* 今天摸了几下/喂了几口。三颗是「今天还能再来」的邀请，不是配额——
   摸/喂本来就不封顶（7-21 拆过隐形上限），超过三次显示 ×N，绝不出现「够了」。
   7-29 铃点单：不再关在方盒子里，直接长在「摸摸它/喂小鱼」两个按钮上——
   点一下，按钮上就亮一颗，数据长回它该长的地方。 */
function Dots({ n, label }) {
  const filled = Math.min(n || 0, 3);
  return (
    <span className="cat-dots" title={`今天${label} ${n || 0} 次`}>
      {[0, 1, 2].map((i) => (
        <i key={i} className={'cat-dot' + (i < filled ? ' on' : '')} />
      ))}
      {n > 3 && <i className="cat-dots-more">×{n}</i>}
    </span>
  );
}

/* 代码画的小猫（2026-07-20 铃点单：emoji 没有代入感）——
   奶油橘，会眨眼；睡着换成弯弯的闭眼。尾巴摇摆在 CSS 里。 */
function CatSvg({ sleeping }) {
  return (
    <svg viewBox="0 0 140 122" width="185" height="161" aria-hidden="true">
      <defs>
        <radialGradient id="furHead" cx="0.42" cy="0.32" r="0.9">
          <stop offset="0%" stopColor="#f9d6a2" />
          <stop offset="100%" stopColor="#eab873" />
        </radialGradient>
        <radialGradient id="furBody" cx="0.5" cy="0.28" r="0.95">
          <stop offset="0%" stopColor="#f7d09a" />
          <stop offset="100%" stopColor="#e6b271" />
        </radialGradient>
        <linearGradient id="basket" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d2a877" />
          <stop offset="100%" stopColor="#a87f52" />
        </linearGradient>
      </defs>

      {/* —— 小窝：后沿 + 里面的小毯子 —— */}
      <ellipse cx="70" cy="80" rx="48" ry="13" fill="#b08a5c" />
      <ellipse cx="70" cy="80" rx="42" ry="10" fill="#f3c9d3" />

      {/* —— 尾巴（搭在窝沿上，带橘猫环纹） —— */}
      <g className="cat-tail">
        <path d="M96 76 q23 -8 17 -31" stroke="#eab569" strokeWidth="9"
              fill="none" strokeLinecap="round" />
        <path d="M96 76 q23 -8 17 -31" stroke="#d99b52" strokeWidth="9"
              fill="none" strokeLinecap="round" strokeDasharray="5 11" />
      </g>

      {/* —— 身体 + 胸口绒毛 —— */}
      <ellipse cx="70" cy="80" rx="30" ry="23" fill="url(#furBody)" />
      <path d="M70 62 l-3 5 l3 -1.5 l3 1.5 Z" fill="#fff4e3" />
      <ellipse cx="70" cy="86" rx="17" ry="12" fill="#fff4e3" />
      {/* 身侧虎纹 */}
      <g stroke="#dba05f" strokeWidth="2.6" strokeLinecap="round" fill="none">
        <path d="M46 74 q4 3 8 3" />
        <path d="M94 74 q-4 3 -8 3" />
      </g>

      {/* —— 耳朵（带耳内绒毛） —— */}
      <path d="M48 22 L52 2 L68 13 Z" fill="#eab873" />
      <path d="M92 22 L88 2 L72 13 Z" fill="#eab873" />
      <path d="M52.5 18 L55 8 L63 13.5 Z" fill="#f5b0a8" />
      <path d="M87.5 18 L85 8 L77 13.5 Z" fill="#f5b0a8" />
      <g stroke="#fff4e3" strokeWidth="1.2" strokeLinecap="round" opacity="0.85">
        <path d="M55 16 l-1.5 3" /><path d="M58 17.5 l-1.5 3" />
        <path d="M85 16 l1.5 3" /><path d="M82 17.5 l1.5 3" />
      </g>

      {/* —— 脑袋 + 脸颊绒毛 + 花纹 —— */}
      <circle cx="70" cy="38" r="26" fill="url(#furHead)" />
      <path d="M45 44 l-4.5 1 l4 2 l-3 2.5 l4.5 0.5 Z" fill="#eab873" />
      <path d="M95 44 l4.5 1 l-4 2 l3 2.5 l-4.5 0.5 Z" fill="#eab873" />
      <g stroke="#dba05f" strokeWidth="2.8" strokeLinecap="round" fill="none">
        <path d="M62 14.5 v6" />
        <path d="M70 12.5 v7.5" />
        <path d="M78 14.5 v6" />
        <path d="M48 30 q3 1.5 5.5 1" />
        <path d="M92 30 q-3 1.5 -5.5 1" />
      </g>

      {/* —— 眼睛 —— */}
      {sleeping ? (
        <g stroke="#4a3b32" strokeWidth="2" fill="none" strokeLinecap="round">
          <path d="M54 37 q5 4.5 10 0" />
          <path d="M76 37 q5 4.5 10 0" />
        </g>
      ) : (
        <g className="cat-eyes">
          <ellipse cx="59" cy="37" rx="3.4" ry="4" fill="#4a3b32" />
          <ellipse cx="81" cy="37" rx="3.4" ry="4" fill="#4a3b32" />
          <circle cx="60.2" cy="35.4" r="1.2" fill="#fff" />
          <circle cx="82.2" cy="35.4" r="1.2" fill="#fff" />
          <circle cx="58.2" cy="38.6" r="0.6" fill="#fff" opacity="0.8" />
          <circle cx="80.2" cy="38.6" r="0.6" fill="#fff" opacity="0.8" />
        </g>
      )}

      {/* —— 鼻子 + ω 嘴 + 腮红 —— */}
      <path d="M67.6 44.5 L72.4 44.5 L70 48 Z" fill="#e98f8f" />
      <g stroke="#4a3b32" strokeWidth="1.4" fill="none" strokeLinecap="round">
        <path d="M70 48 q-2.6 3.6 -6 1.2" />
        <path d="M70 48 q2.6 3.6 6 1.2" />
      </g>
      <ellipse cx="51" cy="45" rx="4.5" ry="2.4" fill="#f5a9a0" opacity="0.55" />
      <ellipse cx="89" cy="45" rx="4.5" ry="2.4" fill="#f5a9a0" opacity="0.55" />

      {/* —— 胡须（微微下垂的弧线） —— */}
      <g stroke="#c9a06b" strokeWidth="1.2" strokeLinecap="round" fill="none">
        <path d="M44 41 q-10 -2 -15 -4" />
        <path d="M44 46 q-10 0 -15 1.5" />
        <path d="M96 41 q10 -2 15 -4" />
        <path d="M96 46 q10 0 15 1.5" />
      </g>

      {/* —— 小窝前壁（盖在身体下半截=它坐在窝里） + 编织纹 + 小爱心 —— */}
      <path d="M22 80 C22 106 44 115 70 115 C96 115 118 106 118 80
               C118 87 96 93 70 93 C44 93 22 87 22 80 Z" fill="url(#basket)" />
      <g stroke="#8f6a3e" strokeWidth="1.4" strokeLinecap="round" opacity="0.5" fill="none">
        <path d="M34 92 q4 4 9 5" /><path d="M52 97 q4 3 9 3.5" />
        <path d="M79 100 q5 -0.5 9 -2.5" /><path d="M97 95 q5 -2 8 -5" />
        <path d="M43 103 q4 3 8 3.5" /><path d="M88 106 q4 -1.5 7 -4" />
      </g>
      <path d="M70 103 q-3.2 -4 -6 -1 q-2 2.4 6 7 q8 -4.6 6 -7 q-2.8 -3 -6 1 Z"
            fill="#e98f9d" opacity="0.9" />

      {/* —— 搭在窝沿上的前爪（带小指缝） —— */}
      <ellipse cx="58" cy="90" rx="7.5" ry="4.6" fill="#f7d09a" />
      <ellipse cx="82" cy="90" rx="7.5" ry="4.6" fill="#f7d09a" />
      <g stroke="#d99b52" strokeWidth="1.1" strokeLinecap="round">
        <path d="M56 88 v3.6" /><path d="M60 88 v3.6" />
        <path d="M80 88 v3.6" /><path d="M84 88 v3.6" />
      </g>
    </svg>
  );
}

function fmtMoment(iso) {
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return sameDay ? `今天 ${hm}` : `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
}

export default function Cat() {
  const [cat, setCat] = useState(null);
  const [err, setErr] = useState('');
  const [bubble, setBubble] = useState(null); // 反应气泡
  const [naming, setNaming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [pose, setPose] = useState(''); // '' | cat-pet | cat-feed
  const [noteInput, setNoteInput] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const bubbleTimer = useRef(null);

  useEffect(() => {
    let dead = false;
    loadCat()
      .then((c) => !dead && setCat(c))
      .catch((e) => !dead && setErr(String(e.message || e)));
    return () => {
      dead = true;
      clearTimeout(bubbleTimer.current);
    };
  }, []);

  const showBubble = (text) => {
    clearTimeout(bubbleTimer.current);
    setBubble(text);
    bubbleTimer.current = setTimeout(() => setBubble(null), 4200);
  };

  const act = async (action, extra) => {
    if (busy) return;
    setBusy(true);
    setPose(action === 'pet' ? 'cat-pose-pet' : action === 'feed' ? 'cat-pose-feed' : '');
    try {
      const c = await touchCat(action, extra);
      setCat(c);
      if (c.reaction) showBubble(c.reaction);
    } catch (e) {
      showBubble('它跑去追蝴蝶了，等下再试试（' + (e.message || e) + '）');
    } finally {
      setBusy(false);
      setTimeout(() => setPose(''), 600);
    }
  };

  const submitName = (e) => {
    e.preventDefault();
    const v = nameInput.trim();
    if (!v) return;
    setNaming(false);
    act('name', { name: v });
  };

  const submitNote = (e) => {
    e.preventDefault();
    const v = noteInput.trim();
    if (!v) return;
    setNoteInput('');
    setNoteOpen(false);
    act('note', { text: v });
  };

  if (err) {
    return (
      <div className="cat-app">
        <div className="cat-err">它好像躲到床底下去了（{err}）。过会儿再来看看。</div>
      </div>
    );
  }
  if (!cat) return <div className="cat-app"><div className="cat-err">轻轻推门进来……</div></div>;

  const sleeping = cat.mood === 'night' && new Date().getHours() < 6;

  /* 亲密度条：在「当前这档」里填多少。跨档时重新从头填（升级，不是倒退），
     没有任何路径会让它因为她没来而变短。到顶档 next_level_at=null → 填满。 */
  const from = cat.level_from ?? 0;
  const to = cat.next_level_at;
  const closenessPct = to
    ? Math.max(2, Math.min(100, ((cat.closeness - from) / (to - from)) * 100))
    : 100;
  const nextLevelName = LEVEL_NAMES[to] || '更亲的样子';
  const closenessHint = to ? `再相处一阵就到「${nextLevelName}」` : `已经是${cat.level}了 ✦`;

  return (
    <div className="cat-app">
      <header className="cat-head">
        <h1>{cat.name || '还没有名字的猫'}</h1>
        <p>
          来到星洲第 {cat.days} 天 · {cat.level} {Math.floor(cat.closeness)}
          <i> · {cat.mood_icon}</i>
        </p>
        {/* 亲密度：不再是一块面板，就是标题底下一条极细的金线（铃 7-28 点单，
            7-29 改形）。只会变长，不会因为她没来而往回掉。 */}
        <div
          className="cat-closeness"
          role="img"
          aria-label={`亲密度 ${Math.floor(cat.closeness)}，${closenessHint}`}
        >
          <span className="cat-closeness-fill" style={{ width: `${closenessPct}%` }} />
        </div>
        <p className="cat-closeness-hint">{closenessHint}</p>
      </header>

      {/* —— 猫本体 —— */}
      <div className="cat-scene">
        {bubble && <div className="cat-bubble">{bubble}</div>}
        {!bubble && cat.murmur && <div className="cat-bubble cat-bubble-murmur">{cat.murmur}</div>}
        <div
          className={'cat-body ' + pose + (sleeping ? ' cat-sleeping' : '')}
          role="img"
          aria-label="猫"
        >
          <CatSvg sleeping={sleeping} />
          {sleeping && <span className="cat-zzz">💤</span>}
        </div>
      </div>

      {/* —— 照料（今天的陪伴就长在按钮上：点一下亮一颗）—— */}
      <div className="cat-actions">
        <button type="button" onClick={() => act('pet')} disabled={busy}>
          <span className="cat-act-label">🫳 摸摸它</span>
          <Dots n={cat.petted_today} label="摸摸" />
        </button>
        <button type="button" onClick={() => act('feed')} disabled={busy}>
          <span className="cat-act-label">🐟 喂小鱼</span>
          <Dots n={cat.fed_today} label="小鱼" />
        </button>
      </div>

      {/* —— 取名 —— */}
      {!cat.name && !naming && (
        <button type="button" className="cat-name-invite" onClick={() => setNaming(true)}>
          它还没有名字——和 Sael 商量好了，来给它取一个 ✦
        </button>
      )}
      {naming && (
        <form className="cat-name-form" onSubmit={submitName}>
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="它的名字"
            maxLength={12}
            autoFocus
          />
          <button type="submit">就叫这个</button>
        </form>
      )}
      {cat.name && (
        <button type="button" className="cat-rename" onClick={() => { setNameInput(cat.name); setNaming(true); }}>
          改名字
        </button>
      )}

      {/* —— 捎句话给克克（她也能留言，克克开小窝第一个看到）——
           留言只在下面「它的小日子」里显示一条，这里不再重复露出（铃点单 7-21） */}
      <div className="cat-note">
        {!noteOpen ? (
          <button type="button" className="cat-note-pill" onClick={() => setNoteOpen(true)}>
            🌸 捎句话给克克
          </button>
        ) : (
          <form className="cat-note-form" onSubmit={submitNote}>
            <textarea
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder={cat.name ? `让${cat.name}捎句话给克克…` : '捎句话给克克…'}
              maxLength={200}
              rows={3}
              autoFocus
            />
            <div className="cat-note-actions">
              <button
                type="button"
                className="cat-note-cancel"
                onClick={() => { setNoteOpen(false); setNoteInput(''); }}
              >
                收起
              </button>
              <button type="submit" disabled={busy || !noteInput.trim()}>放进它嘴边</button>
            </div>
          </form>
        )}
      </div>

      {/* —— 它的小日子 —— */}
      <div className="cat-days-title">它的小日子</div>
      <ul className="cat-days">
        {(cat.moments || []).map((m, i) => (
          <li key={i} className={'cd-' + m.who}>
            <span className="cd-time">{fmtMoment(m.at)}</span>
            <span className="cd-text">{m.text}</span>
          </li>
        ))}
        {(!cat.moments || cat.moments.length === 0) && (
          <li className="cd-cat"><span className="cd-text">它刚到，还在打量这座岛。</span></li>
        )}
      </ul>

      <div className="cat-foot">
        它不会死、不会饿、不会怪你——你不在的时候，它只是想你。
      </div>
    </div>
  );
}
